'use client';

import Link from 'next/link';
import { useParams } from 'next/navigation';
import { Clock, MapPin, Phone } from '@phosphor-icons/react/dist/ssr';
import { ThemeProvider } from '@/components/public/theme-provider';
import { PublicNavbar } from '@/components/public/public-navbar';
import { PublicSEO } from '@/components/public/public-seo';
import { OffersBanner } from '@/components/public/offers-banner';
import { PoweredByDineator } from '@/components/public/powered-by-dineator';
import { resolveThemeConfig } from '@/types/theme';
import type { PublicMenuData, MenuItemData } from '@/types/public-menu';
import { getContextualHref } from '@/lib/domains';
import { getCurrencySymbol } from '@/lib/currency';
import { formatBusinessHoursRange, getOpenStatus } from '@/lib/business-hours';
import { HOME_CSS } from './home/home-css';
import { HomeHero } from './home/HomeHero';
import { ServiceStrip, buildServices } from './home/ServiceStrip';
import { AisleTiles } from './home/AisleTiles';
import { DealsStrip } from './home/DealsStrip';
import { ProductRow, mrpOf } from './home/ProductRow';
import type { StoreInfo, StoreDeal } from './home/home-data';

const API_BASE = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000/api';

export default function GroceryHome({ data, info, deals }: { data: PublicMenuData; info?: StoreInfo | null; deals?: StoreDeal[] | null }) {
  const params = useParams();
  const restaurant = data.restaurant;
  const slug = (params?.slug as string) || restaurant.slug;
  const theme = resolveThemeConfig(data.themeConfig ?? null);
  const { tokens } = theme;
  const flags = data.featureFlags ?? {};
  const sym = getCurrencySymbol(flags.currency || restaurant.config?.currency);
  const ordering = flags.orderingEnabled !== false;

  const hours = restaurant.config?.businessHours ?? flags.businessHours;
  const status = getOpenStatus(hours, flags.timezone ?? 'UTC');
  const delivery = ordering && flags.enableDelivery !== false && restaurant.config?.enableDelivery !== false;
  const pickup = ordering && flags.enableTakeaway !== false && restaurant.config?.enableTakeaway !== false;

  const aisles = data.categories.filter((c) => c.menuItems.length > 0);
  const all: MenuItemData[] = aisles.flatMap((c) => c.menuItems).filter((i) => i.isAvailable);
  // Fresh picks: one photographed product per aisle round-robin, so it spans the store.
  const fresh: MenuItemData[] = [];
  for (let n = 0; fresh.length < 12 && n < 3; n++) {
    for (const c of aisles) {
      const it = c.menuItems.filter((i) => i.isAvailable && i.imageUrl)[n];
      if (it && fresh.length < 12) fresh.push(it);
    }
  }
  const onOffer = all.filter((i) => !i.markdownPct && mrpOf(i)).slice(0, 12);
  const useSoon = all.filter((i) => i.markdownPct).slice(0, 12);
  const logo = restaurant.logoUrl && /^https?:/.test(restaurant.logoUrl) ? restaurant.logoUrl : undefined;

  const services = buildServices({ sym, delivery, pickup, fee: info?.deliveryFee, freeOver: info?.freeDeliveryThreshold, requireSlot: info?.requireSlot && (delivery || pickup), min: info?.minOrderAmount });
  const valueLine = [
    delivery && pickup ? 'Delivery & pickup' : delivery ? 'Delivery' : pickup ? 'Pickup' : '',
    delivery && info?.freeDeliveryThreshold ? `Free delivery over ${sym}${info.freeDeliveryThreshold.toFixed(2)}` : '',
    info?.requireSlot && (delivery || pickup) ? 'Book a delivery slot' : '',
    info?.minOrderAmount ? `Minimum basket ${sym}${info.minOrderAmount.toFixed(2)}` : '',
  ].filter(Boolean);

  return (
    <ThemeProvider config={theme}>
      <style>{HOME_CSS}</style>
      <PublicSEO restaurant={restaurant} tokens={tokens} pageType="home" />
      <OffersBanner restaurantId={restaurant.id} apiBase={API_BASE} tokens={tokens} />
      <div className="theme-body" style={{ minHeight: '100dvh', background: 'var(--color-bg)', overflowX: 'hidden', paddingBottom: 72 }}>
        <PublicNavbar
          restaurant={{ ...restaurant, logoUrl: logo, config: { enableReservations: false } }}
          tokens={tokens}
          activePage="home"
        />

        <HomeHero slug={slug} name={restaurant.name} description={restaurant.description} logo={logo} status={status} valueLine={valueLine} ordering={ordering} collage={fresh.filter((i) => i.imageUrl)} aisles={aisles} sym={sym} />
        <ServiceStrip items={services} />
        <AisleTiles aisles={aisles} slug={slug} />
        <DealsStrip deals={deals ?? []} slug={slug} sym={sym} />
        <ProductRow title="Fresh picks" items={fresh} slug={slug} sym={sym} />
        <ProductRow title="On offer" items={onOffer} slug={slug} sym={sym} />
        <ProductRow title="Use soon — reduced" hint="Close to their date, priced to go" items={useSoon} slug={slug} sym={sym} />

        <section aria-label="Store details" className="gh-wrap gh-section">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, padding: 18, background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', fontSize: '0.9rem', color: 'var(--color-text)' }}>
            {restaurant.address && <div style={{ display: 'flex', gap: 10 }}><MapPin size={18} aria-hidden style={{ flexShrink: 0, color: 'var(--color-primary)' }} /><span>{restaurant.address}</span></div>}
            {restaurant.phone && <div style={{ display: 'flex', gap: 10 }}><Phone size={18} aria-hidden style={{ flexShrink: 0, color: 'var(--color-primary)' }} /><a href={`tel:${restaurant.phone}`} style={{ color: 'inherit' }}>{restaurant.phone}</a></div>}
            <div style={{ display: 'flex', gap: 10 }}><Clock size={18} aria-hidden style={{ flexShrink: 0, color: 'var(--color-primary)' }} /><span>{hours && hours.length ? formatBusinessHoursRange(hours) : 'Store hours not listed yet'}</span></div>
            {ordering && <Link href={getContextualHref(slug, '/track')} className="gh-link">Track your order</Link>}
          </div>
        </section>

        <footer className="gh-wrap" style={{ marginTop: 32, textAlign: 'center', fontSize: '0.75rem', color: 'var(--color-text-muted)', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span>© {new Date().getFullYear()} {restaurant.name}</span>
          <PoweredByDineator grocery />
        </footer>
      </div>
    </ThemeProvider>
  );
}
